/**
 * BedingungenPage — Bedingungsbaum.
 *
 * Picks an FV + Format + Prüfidentifikator, fetches the AHB doc, and lets the
 * user EITHER pick a line whose `ahb_expression` carries condition references
 * (`[…]`) OR type an expression by hand. The expression is parsed
 * (`parseAhbExpression`) and resolved against the doc's `pruefungen`/`pakete`
 * into a positioned mind-map (`buildRegelTree`), drawn as an SVG WENN/DANN tree
 * (`RegelTree`). A side panel shows the leading status word and a colour legend
 * for the leaf badges (FORMAT / VORAUSSETZUNG / PAKET). We do NOT evaluate the
 * expression, so no pass/fail is claimed.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, Select, SelectItem, Notice } from "@conuti-das/prince-ui";
import { Flex, Spinner } from "@/ui-prince";
import { regelwerkApi } from "@/lib/regelwerkApi";
import { parseAhbExpression } from "@/lib/ahbExpression";
import type { AhbDoc, AhbExprNode, AhbLine, ConditionKind } from "@/types/regelwerk";
import { RegelTree, buildRegelTree } from "@/components/RegelTree";
import artifactsData from "@/data/generated/artifacts.json";
import type { ArtifactIndex } from "@/types";
import { standOptions } from "./TabellenPage";
import { FvNote, useFvBinding } from "./fvBinding";
import "./bedingungen.css";

const MONTHS = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];
function fvLabel(fv: string): string {
  const m = /^(\d{4})(\d{2})$/.exec(fv);
  if (!m) return fv;
  const month = MONTHS[Number(m[2]) - 1] ?? m[2];
  return `${month} ${m[1]} (FV${fv.slice(2)})`;
}

/** A line is "interesting" for the tree when its expression references a `[…]`. */
function hasRefs(line: AhbLine): boolean {
  return (line.ahb_expression ?? "").includes("[");
}

function lineLabel(line: AhbLine): string {
  const seg = [line.segment_group_key, line.segment_code, line.data_element]
    .filter(Boolean)
    .join(" · ");
  return `${seg}  —  ${line.ahb_expression}`;
}

/**
 * Build a `resolve` closure over the doc's pruefungen + pakete.
 *  - package ids resolve to kind "paket"
 *  - text starting with "Format" (case-insensitive) → "format"
 *  - otherwise a known id → "voraussetzung"
 *  - unknown id → kind "unknown", text "—"
 * Note: the caller (buildRegelTree) already forces package *nodes* to "paket";
 * here we additionally tag ids that appear in `pakete`.
 */
function makeResolver(doc: AhbDoc | null) {
  const pruef = new Map<string, string>();
  const pakete = new Map<string, string>();
  for (const c of doc?.pruefungen ?? []) pruef.set(c.id, c.text);
  for (const p of doc?.pakete ?? []) pakete.set(p.id, p.text);
  // UB-Bedingungen (`[UB1]`) are package-like named sub-expressions — resolve
  // and classify them exactly like Pakete so the tree expands them too.
  for (const u of doc?.ub_bedingungen ?? []) pakete.set(u.id, u.text);

  return (key: string): { text: string; kind: ConditionKind } => {
    if (pakete.has(key)) {
      return { text: pakete.get(key)!, kind: "paket" };
    }
    if (pruef.has(key)) {
      const text = pruef.get(key)!;
      const kind: ConditionKind = /^\s*format/i.test(text) ? "format" : "voraussetzung";
      return { text, kind };
    }
    return { text: "—", kind: "unknown" };
  };
}

/** Collect all `[…]` reference keys of an expression tree (document order,
 *  deduped) — feeds the "Bedingungen im Ausdruck" panel. */
export function collectRefKeys(node: AhbExprNode | null, out: string[] = []): string[] {
  if (!node) return out;
  if (node.type === "ref" || node.type === "package") {
    if (!out.includes(node.key)) out.push(node.key);
    return out;
  }
  for (const child of node.children) collectRefKeys(child, out);
  return out;
}

const LEGEND: { kind: ConditionKind; label: string }[] = [
  { kind: "format", label: "Formatprüfung" },
  { kind: "voraussetzung", label: "Voraussetzung" },
  { kind: "paket", label: "Paket" },
  { kind: "unknown", label: "Unbekannt" },
];

export function BedingungenPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [fvList, setFvList] = useState<string[]>([]);
  // FV-Achse = Kopfzeile (siehe `fvBinding`); der Stand darunter bleibt lokal.
  const { fv, setFv, seedFv, note } = useFvBinding(fvList, searchParams.get("fv"));
  const [fmt, setFmt] = useState<string>(searchParams.get("fmt") ?? "");
  const [pruefid, setPruefid] = useState<string>(searchParams.get("pruefid") ?? "");

  // ---- Stände des gewählten AHB-Formats (build-time Artefakt-Index) --------
  const artifacts = artifactsData as ArtifactIndex;
  const standIdx = artifacts.ahbStaende?.[fv];
  const staende = standOptions(standIdx, fmt);
  const current = standIdx?.[fmt]?.current ?? "";
  const [stand, setStand] = useState<string>("");
  const effectiveStand =
    stand && staende.some((s) => s.stand === stand) ? stand : current;
  const isCurrent = !current || effectiveStand === current;
  useEffect(() => {
    setStand("");
  }, [fv, fmt]);

  /** ahb map of the FV: { FORMAT: [pruefid...] } */
  const [docs, setDocs] = useState<Record<string, string[]>>({});
  const [doc, setDoc] = useState<AhbDoc | null>(null);

  // expression source: either a picked line index or a free-typed expression.
  // A deep-linked `expr` (jump from the AHB-Tabellen view) seeds the input and
  // must survive the doc-load default below — tracked via `seededRef`.
  const [lineIdx, setLineIdx] = useState<string>("");
  const [exprText, setExprText] = useState<string>(searchParams.get("expr") ?? "");
  const seededRef = useRef<boolean>(Boolean(searchParams.get("expr")));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // --- load the index of formatversions -----------------------------------
  useEffect(() => {
    let cancelled = false;
    regelwerkApi
      .getDocIndex()
      .then((idx) => {
        if (cancelled) return;
        const list = [...(idx.doctypes?.ahb?.formatversions ?? [])].sort().reverse();
        setFvList(list);
        seedFv(list[0] ?? "");
      })
      .catch((e) => !cancelled && setError(`Index konnte nicht geladen werden: ${String(e)}`));
    return () => {
      cancelled = true;
    };
  }, [seedFv]);

  // --- load format/pruefid options of the FV ------------------------------
  useEffect(() => {
    if (!fv) return;
    let cancelled = false;
    regelwerkApi
      .listDocs(fv)
      .then((d) => {
        if (cancelled) return;
        const ahb = d.ahb ?? {};
        setDocs(ahb);
        const formats = Object.keys(ahb).sort();
        // Prefer a format that actually has pruefids (APERAK/CONTRL have none).
        const firstNonEmpty = formats.find((f) => (ahb[f]?.length ?? 0) > 0) ?? formats[0] ?? "";
        setFmt((cur) => (cur && formats.includes(cur) ? cur : firstNonEmpty));
      })
      .catch((e) => !cancelled && setError(`Dokumentliste (${fv}) fehlgeschlagen: ${String(e)}`));
    return () => {
      cancelled = true;
    };
  }, [fv]);

  const formats = useMemo(() => Object.keys(docs).sort(), [docs]);
  const pruefids = useMemo(() => [...(docs[fmt] ?? [])].sort(), [docs, fmt]);

  // keep pruefid valid when format/list changes
  useEffect(() => {
    if (pruefids.length && !pruefids.includes(pruefid)) setPruefid(pruefids[0]);
  }, [pruefids, pruefid]);

  // reflect selection in the URL (shareable links, incl. the expression)
  useEffect(() => {
    const next: Record<string, string> = {};
    if (fv) next.fv = fv;
    if (fmt) next.fmt = fmt;
    if (pruefid) next.pruefid = pruefid;
    if (exprText.trim()) next.expr = exprText;
    setSearchParams(next, { replace: true });
  }, [fv, fmt, pruefid, exprText, setSearchParams]);

  // --- fetch the AHB doc ----------------------------------------------------
  useEffect(() => {
    if (!fv || !fmt || !pruefid) {
      setDoc(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDoc(null);
    regelwerkApi
      .getAhbDoc(fv, fmt, pruefid, isCurrent ? undefined : effectiveStand)
      .then((d) => {
        if (cancelled) return;
        setDoc(d);
      })
      .catch((e) => {
        if (cancelled) return;
        setDoc(null);
        setError(`AHB-Dokument konnte nicht geladen werden: ${String(e)}`);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [fv, fmt, pruefid, effectiveStand, isCurrent]);

  // lines that carry condition references
  const exprLines = useMemo(
    () => (doc?.lines ?? []).map((l, i) => ({ line: l, i })).filter(({ line }) => hasRefs(line)),
    [doc],
  );

  // when the doc (or its expr-lines) changes, default to the first such line.
  // A URL-seeded expression wins exactly once: we keep it, and if it matches a
  // line of the loaded doc we select that line in the dropdown.
  useEffect(() => {
    if (seededRef.current) {
      if (exprLines.length === 0) return; // doc not loaded yet — keep the seed
      seededRef.current = false;
      const match = exprLines.find(({ line }) => line.ahb_expression === exprText);
      if (match) setLineIdx(String(match.i));
      return;
    }
    if (exprLines.length === 0) {
      setLineIdx("");
      setExprText("");
      return;
    }
    const first = exprLines[0];
    setLineIdx(String(first.i));
    setExprText(first.line.ahb_expression);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exprLines]);

  // when the user picks a different line, seed the input with its expression
  const onPickLine = (key: string | number | null) => {
    if (key == null) return;
    const value = String(key);
    setLineIdx(value);
    const found = exprLines.find(({ i }) => String(i) === value);
    if (found) setExprText(found.line.ahb_expression);
  };

  const resolve = useMemo(() => makeResolver(doc), [doc]);
  const parsed = useMemo(() => parseAhbExpression(exprText), [exprText]);
  const graph = useMemo(
    () => (exprText.trim() ? buildRegelTree(parsed, resolve) : null),
    [parsed, resolve, exprText],
  );

  /** the referenced conditions with their resolved texts (panel list) */
  const refList = useMemo(
    () =>
      collectRefKeys(parsed.tree).map((key) => ({ key, ...resolve(key) })),
    [parsed, resolve],
  );

  const ready = Boolean(fv && fmt && pruefid);

  return (
    <div className="page bedingungen-page">
      <div className="page-header">
        <h2>Bedingungsbaum</h2>
        <div className="page-subtitle">
          Stellt einen AHB-Ausdruck als WENN/DANN-Baum dar: jeder Verweis
          <code> [931]</code> wird über die Prüfungen und Pakete des Dokuments aufgelöst
          und farblich nach Art gekennzeichnet.
        </div>
      </div>

      <FvNote note={note} />

      <Card>
        <Flex className="bedingungen-toolbar">
          <div className="bedingungen-field">
            <label htmlFor="bed-fv">Formatversion</label>
            <Select
              id="bed-fv"
              style={{ minWidth: "220px" }}
              selectedKey={fv || null}
              onSelectionChange={(key) => {
                if (key) setFv(String(key));
              }}
            >
              {fvList.map((f) => (
                <SelectItem key={f} id={f}>
                  {fvLabel(f)}
                </SelectItem>
              ))}
            </Select>
          </div>

          {staende.length > 0 && (
            <div className="bedingungen-field">
              <label htmlFor="bed-stand">Stand</label>
              <Select
                id="bed-stand"
                style={{ minWidth: "200px" }}
                selectedKey={effectiveStand || null}
                onSelectionChange={(key) => key && setStand(String(key))}
              >
                {staende.map((s) => (
                  <SelectItem key={s.stand} id={s.stand}>
                    {s.stand === current ? `${s.label} (aktuell)` : s.label}
                  </SelectItem>
                ))}
              </Select>
            </div>
          )}

          <div className="bedingungen-field">
            <label htmlFor="bed-fmt">Format</label>
            <Select
              id="bed-fmt"
              style={{ minWidth: "160px" }}
              selectedKey={fmt || "__empty__"}
              onSelectionChange={(key) => {
                if (key && key !== "__empty__") setFmt(String(key));
              }}
            >
              {formats.length === 0
                ? [<SelectItem key="__empty__" id="__empty__">—</SelectItem>]
                : formats.map((f) => (
                    <SelectItem key={f} id={f}>
                      {f}
                    </SelectItem>
                  ))}
            </Select>
          </div>

          <div className="bedingungen-field">
            <label htmlFor="bed-pruefid">Prüfidentifikator</label>
            <Select
              id="bed-pruefid"
              style={{ minWidth: "160px" }}
              selectedKey={pruefid || "__empty__"}
              onSelectionChange={(key) => {
                if (key && key !== "__empty__") setPruefid(String(key));
              }}
            >
              {pruefids.length === 0
                ? [<SelectItem key="__empty__" id="__empty__">—</SelectItem>]
                : pruefids.map((p) => (
                    <SelectItem key={p} id={p}>
                      {p}
                    </SelectItem>
                  ))}
            </Select>
          </div>

          {exprLines.length > 0 && (
            <div className="bedingungen-field bedingungen-field--grow">
              <label htmlFor="bed-line">Zeile</label>
              <Select
                id="bed-line"
                style={{ minWidth: "100%" }}
                selectedKey={lineIdx || null}
                onSelectionChange={onPickLine}
              >
                {exprLines.map(({ line, i }) => (
                  <SelectItem key={String(i)} id={String(i)}>
                    {lineLabel(line)}
                  </SelectItem>
                ))}
              </Select>
            </div>
          )}

          <div className="bedingungen-field bedingungen-field--grow">
            <label htmlFor="bed-expr">Ausdruck</label>
            <input
              id="bed-expr"
              className="prn-input"
              value={exprText}
              placeholder="z. B. X [931] [494]"
              style={{ width: "100%" }}
              onChange={(e) => setExprText(e.target.value)}
            />
          </div>
        </Flex>
      </Card>

      {error && (
        <div style={{ marginTop: "var(--s-3)" }}>
          <Notice tone="negative">{error}</Notice>
        </div>
      )}

      {loading && (
        <div style={{ margin: "var(--s-4)", display: "flex", alignItems: "center", gap: "var(--s-2)" }}>
          <Spinner active size="m" />
          <span>Lade AHB-Dokument …</span>
        </div>
      )}

      {!loading && !ready && !error && (
        <div style={{ marginTop: "var(--s-3)" }}>
          <Notice tone="info">Wähle Formatversion, Format und Prüfidentifikator.</Notice>
        </div>
      )}

      {!loading && ready && doc && !graph && !error && (
        <div style={{ marginTop: "var(--s-3)" }}>
          <Notice tone="info">Wähle eine Zeile mit Bedingungen oder gib einen Ausdruck ein.</Notice>
        </div>
      )}

      {!loading && graph && (
        <div className="bedingungen-body" style={{ marginTop: "var(--s-3)" }}>
          <div className="bedingungen-canvas-wrap">
            <RegelTree graph={graph} />
          </div>

          <aside className="bedingungen-panel">
            <div>
              <div className="bedingungen-status">
                <span className="bedingungen-status-word">
                  {parsed.status ? `→ ${parsed.status}` : "—"}
                </span>
              </div>
              <div className="bedingungen-status-caption">
                Status des Ausdrucks (nicht ausgewertet)
              </div>
            </div>

            {refList.length > 0 && (
              <div>
                <div className="bedingungen-legend-title">Bedingungen im Ausdruck</div>
                <div className="bedingungen-reflist">
                  {refList.map((r) => (
                    <div key={r.key} className="bedingungen-reflist-item">
                      <span className={`bedingungen-swatch bedingungen-swatch--${r.kind}`} />
                      <span className="bedingungen-reflist-key">[{r.key}]</span>
                      <span className="bedingungen-reflist-text">{r.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="bedingungen-legend-title">Legende</div>
              <div className="bedingungen-legend">
                {LEGEND.map((l) => (
                  <div key={l.kind} className="bedingungen-legend-item">
                    <span className={`bedingungen-swatch bedingungen-swatch--${l.kind}`} />
                    {l.label}
                  </div>
                ))}
              </div>
            </div>

            <div className="bedingungen-note">
              Der Baum zeigt nur die Struktur der Bedingungen. Es findet keine
              Auswertung statt — es wird also keine Aussage über bestanden/nicht
              bestanden getroffen.
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
